// Komponente für die durchsuchbare Länderauswahl
import React, {useState, useEffect} from "react";
import {Form} from "react-bootstrap";

export interface Option {
    value: string;
    label: string;
}

export interface CountrySelectProps {
    value: string;
    onChange: (value: string) => void;
    onBlur: () => void;
    options: Option[];
    isInvalid?: boolean;
    placeholder: string;
    errorMessage?: string;
    noResultsText?: string;
}

// Standard-Länderliste als Export für Rückwärtskompatibilität
export const COUNTRIES: Option[] = [
    { value: "AT", label: "🇦🇹 Austria" },
    { value: "BE", label: "🇧🇪 Belgium" },
    { value: "BA", label: "🇧🇦 Bosnia and Herzegovina" },
    { value: "BG", label: "🇧🇬 Bulgaria" },
    { value: "HR", label: "🇭🇷 Croatia" },
    { value: "CY", label: "🇨🇾 Cyprus" },
    { value: "CZ", label: "🇨🇿 Czech Republic" },
    { value: "DK", label: "🇩🇰 Denmark" },
    { value: "DE", label: "🇩🇪 Deutschland" },
    { value: "EE", label: "🇪🇪 Estonia" },
    { value: "FI", label: "🇫🇮 Finland" },
    { value: "FR", label: "🇫🇷 France" },
    { value: "GR", label: "🇬🇷 Greece" },
    { value: "HU", label: "🇭🇺 Hungary" },
    { value: "IE", label: "🇮🇪 Ireland" },
    { value: "IT", label: "🇮🇹 Italy" },
    { value: "LV", label: "🇱🇻 Latvia" },
    { value: "LT", label: "🇱🇹 Lithuania" },
    { value: "LU", label: "🇱🇺 Luxembourg" },
    { value: "MT", label: "🇲🇹 Malta" },
    { value: "MD", label: "🇲🇩 Moldova" },
    { value: "MK", label: "🇲🇰 North Macedonia" },
    { value: "GB-NIR", label: "🇬🇧 Northern Ireland" },
    { value: "NL", label: "🇳🇱 Netherlands" },
    { value: "PL", label: "🇵🇱 Poland" },
    { value: "PT", label: "🇵🇹 Portugal" },
    { value: "RO", label: "🇷🇴 Romania" },
    { value: "RS", label: "🇷🇸 Serbia" },
    { value: "SGP", label: "🇸🇬 Singapore" },
    { value: "SK", label: "🇸🇰 Slovakia" },
    { value: "SI", label: "🇸🇮 Slovenia" },
    { value: "ES", label: "🇪🇸 Spain" },
    { value: "SE", label: "🇸🇪 Sweden" },
    { value: "CH", label: "🇨🇭 Switzerland" },
    { value: "GB", label: "🇬🇧 United Kingdom of Great Britain (excluding Northern Ireland)" },
    { value: "USA", label: "🇺🇸 United States of America" }
];

export const SearchAndSelect: React.FC<CountrySelectProps> = ({
                                                                value,
                                                                onChange,
                                                                onBlur,
                                                                options,
                                                                isInvalid,
                                                                placeholder,
                                                                errorMessage,
                                                                noResultsText = "Keine Ergebnisse gefunden",
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState<Option | null>(null);

    // Aktualisiere den angezeigten Wert wenn sich der externe value ändert
    useEffect(() => {
        const option = options.find(o => o.value === value);
        setSelectedOption(option || null);
        if (option) {
            setSearchTerm(option.label);
        } else {
            setSearchTerm("");
        }
    }, [value, options]);

    // Filtere Optionen basierend auf dem Suchbegriff
    const filteredOptions = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const term = e.target.value;
        setSearchTerm(term);
        setIsOpen(true);

        // Wenn der Suchbegriff leer ist, setze die ausgewählte Option zurück
        if (term === "") {
            onChange("");
            setSelectedOption(null);
        }
    };

    const handleOptionSelect = (option: Option) => {
        setSelectedOption(option);
        setSearchTerm(option.label);
        onChange(option.value);
        setIsOpen(false);
    };

    const handleInputBlur = () => {
        // Verzögerung, damit onClick auf Optionen noch funktioniert
        setTimeout(() => {
            setIsOpen(false);
            onBlur();

            // Wenn keine Option ausgewählt wurde, leere das Feld
            if (!selectedOption && searchTerm) {
                setSearchTerm("");
                onChange("");
            }
        }, 150);
    };

    const handleInputFocus = () => {
        setIsOpen(true);
    };

    return (
        <div style={{ position: "relative" }}>
            <Form.Control
                type="text"
                value={searchTerm}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                placeholder={placeholder}
                isInvalid={isInvalid}
                autoComplete="off"
            />
            {isInvalid && errorMessage && (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                    {errorMessage}
                </div>
            )}

            {isOpen && filteredOptions.length > 0 && (
                <div
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        // Wir nutzen hier ein Template-Literal für !important, falls nötig
                        backgroundColor: "#2b3035",
                        border: "1px solid #495057",
                        borderTop: "none",
                        borderRadius: "0 0 0.25rem 0.25rem",
                        maxHeight: "200px",
                        overflowY: "auto",
                        zIndex: 9999, // Extrem hoch, damit es über allem liegt
                        boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                    }}
                >
                    {filteredOptions.map((option) => (
                        <div
                            key={option.value}
                            style={{
                                padding: "10px 12px",
                                cursor: "pointer",
                                color: "white",
                                backgroundColor: selectedOption?.value === option.value ? "#3d444d" : "#2b3035",
                                borderBottom: "1px solid #3c4146"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#3d444d";
                                e.currentTarget.style.color = "white";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = selectedOption?.value === option.value ? "#3d444d" : "#2b3035";
                                e.currentTarget.style.color = "white";
                            }}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleOptionSelect(option);
                            }}
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            )}

            {isOpen && filteredOptions.length === 0 && searchTerm && (
                <div
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        backgroundColor: "#2b3035", // Gleiches Dunkelgrau wie oben
                        border: "1px solid #495057",
                        borderTop: "none",
                        borderRadius: "0 0 0.25rem 0.25rem",
                        padding: "10px 12px",
                        color: "#adb5bd",           // Ein dezentes Hellgrau für den Text
                        zIndex: 9999,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.5)"
                    }}
                >
                    {noResultsText}
                </div>
            )}
        </div>
    );
};